import React from "react";
import {format} from "date-fns";
import {useTranslation} from "react-i18next";
import {Button} from "primereact/button";
import {confirmPopup} from "primereact/confirmpopup";
import i18n from '../Translations/i18n';
import {Tooltip} from "primereact/tooltip";
import {Tag} from "primereact/tag";

const getNestedValue = (obj: any, path: string) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

const tableTranslationTemplate = function (rowData: {
    [x: string]: any;
    active: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactPortal | Iterable<React.ReactNode> | null | undefined;
}, item: { field: string }) {
    let value = getNestedValue(rowData, item.field) as string;
    return useTranslation(value);
};

const tableBooleanTemplate = function (rowData: {
    [x: string]: any;
    active: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactPortal | Iterable<React.ReactNode> | null | undefined;
}, item: { field: string }) {
    let value = getNestedValue(rowData, item.field) as boolean;
    // let value = rowData[item.field] as boolean;
    return value ? <span className="pi pi-check"/> : <span className="pi pi-times"/>;
};

const tableIconTemplate = function (rowData: {
    [x: string]: any;
    active: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactPortal | Iterable<React.ReactNode> | null | undefined;
}, item: { field: string }) {
    let value = getNestedValue(rowData, item.field);
    // let value = rowData[item.field] as boolean;
    return <span className={value}/>;
};

const tableDecimalTemplate = function (rowData: {
    [x: string]: any;
    active: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactPortal | Iterable<React.ReactNode> | null | undefined;
}, item: { field: string }) {
    return Number.parseFloat(getNestedValue(rowData, item.field) ?? 0).toFixed(2);
};

const tableDurationTemplate = function (rowData: {
    [x: string]: any;
    active: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactPortal | Iterable<React.ReactNode> | null | undefined;
}, item: { field: string }) {
    let nestedValue = getNestedValue(rowData, item.field);
    return nestedValue !== null && nestedValue !== undefined ? new Date(nestedValue * 1000).toISOString().slice(11, 19) : null;
};

const tableNumberTemplate = function (rowData: {
    [x: string]: any;
    active: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactPortal | Iterable<React.ReactNode> | null | undefined;
}, item: { field: string }) {
    return Number.parseInt(getNestedValue(rowData, item.field) ?? 0);
};

const getUTCOffset = (date: Date) => {
    const offset = -date.getTimezoneOffset();
    const sign = offset >= 0 ? '+' : '-';
    const hours = Math.abs(Math.floor(offset / 60));
    const minutes = Math.abs(offset % 60);
    return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const tableDateTimeTemplate = function (rowData: {
    [x: string]: any;
    active: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactPortal | Iterable<React.ReactNode> | null | undefined;
}, item: { field: string }) {
    if (getNestedValue(rowData, item.field) == null) return null;

    const date = new Date(getNestedValue(rowData, item.field));
    const formattedDate = format(date, 'dd-MM-yyyy HH:mm');

    return (
        <>
            <Tooltip target="[data-pr-tooltip]"/>
            <span id={`tooltip-${getNestedValue(rowData, item.field)}`} data-pr-tooltip={`UTC ${getUTCOffset(date)}`} data-pr-position="right">
                {formattedDate}
            </span>
        </>
    );
};

const tableDateTemplate = function (rowData: {
    [x: string]: any;
    active: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactPortal | Iterable<React.ReactNode> | null | undefined;
}, item: { field: string }) {
    if (getNestedValue(rowData, item.field) == null) return null;

    const date = new Date(getNestedValue(rowData, item.field));
    const formattedDate = format(date, 'dd-MM-yyyy');

    return (
        <>
            <Tooltip target="[data-pr-tooltip]"/>
            <span id={`tooltip-${getNestedValue(rowData, item.field)}`} data-pr-tooltip={`UTC ${getUTCOffset(date)}`} data-pr-position="right">
                {formattedDate}
            </span>
        </>
    );
};

const tableWeekdayTemplate = function (rowData: {
    [x: string]: any;
    active: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactPortal | Iterable<React.ReactNode> | null | undefined;
}, item: { field: string }) {
    if (getNestedValue(rowData, item.field) == null) return null;

    const date = new Date(getNestedValue(rowData, item.field));
    const formattedWeekday = date.toLocaleDateString(undefined, { weekday: 'long' }); // 'undefined' uses browser locale

    return (
        <>
            <Tooltip target="[data-pr-tooltip]"/>
            <span id={`tooltip-${getNestedValue(rowData, item.field)}`} data-pr-tooltip={`UTC ${getUTCOffset(date)}`} data-pr-position="right">
                {formattedWeekday}
            </span>
        </>
    );
};


const tableTagTemplate = function (rowData: {
	[x: string]: any;
	active: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactPortal | Iterable<React.ReactNode> | null | undefined;
}, item: { field: string }) {
	if (getNestedValue(rowData, item.field) == null) return null;
	let value = getNestedValue(rowData, item.field);
	return (
		<>
			<Tag severity={getSeverityFromText(value)} value={value}/>
		</>
	);
};

const tableTagRandomColorTemplate = function (rowData: {
	[x: string]: any;
	active: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactPortal | Iterable<React.ReactNode> | null | undefined;
}, item: { field: string }) {
	if (getNestedValue(rowData, item.field) == null) return null;
	let value = getNestedValue(rowData, item.field);
	return (
		<>
			<Tag style={{background: getColorFromText(value)}} value={value}/>
		</>
	);
};


const getSeverityFromText = (value: any): "contrast" | "info" | "secondary" | "success" | "warning" | "danger" | null | undefined => {
	if (value == null) return 'contrast';

	const str = String(value);
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		hash = str.charCodeAt(i) + ((hash << 5) - hash);
		hash |= 0; // convert to 32bit int
	}

	const index = Math.abs(hash) % 6;

	const severities = [
		'info',
		'secondary',
		'danger',
		'success',
		'contrast',
		'warning',
	] as const;

	return severities[index];
};

const getColorFromText = (value: any): string => {
	if (value == null) return '#999999';

	const str = String(value);

	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		hash = str.charCodeAt(i) + ((hash << 5) - hash);
		hash |= 0;
	}

	// Maak RGB uit hash
	const r = (hash >> 0) & 0xff;
	const g = (hash >> 8) & 0xff;
	const b = (hash >> 16) & 0xff;

	// Zorg dat kleuren niet te donker zijn (optioneel tweakbaar)
	const normalize = (c: number) => Math.floor((c + 256) / 2);

	return `rgb(${normalize(r)}, ${normalize(g)}, ${normalize(b)})`;
};

const tableTimeTemplate = function (rowData: {
    [x: string]: any;
    active: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactPortal | Iterable<React.ReactNode> | null | undefined;
}, item: { field: string }) {
    if (getNestedValue(rowData, item.field) == null) return null;

    const date = new Date(getNestedValue(rowData, item.field));
    const formattedDate = format(date, 'HH:mm:ss');

    return (
        <>
            <Tooltip target="[data-pr-tooltip]"/>
            <span id={`tooltip-${getNestedValue(rowData, item.field)}`} data-pr-tooltip={`UTC ${getUTCOffset(date)}`} data-pr-position="right">
                {formattedDate}
            </span>
        </>
    );
};

const tableTimeDetailedTemplate = function (rowData: {
    [x: string]: any;
    active: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactPortal | Iterable<React.ReactNode> | null | undefined;
}, item: { field: string }) {
    if (getNestedValue(rowData, item.field) == null) return null;

    const date = new Date(getNestedValue(rowData, item.field));
    const formattedDate = format(date, 'HH:mm:ss');

    return (
        <>
            <Tooltip target="[data-pr-tooltip]"/>
            <span id={`tooltip-${getNestedValue(rowData, item.field)}`} data-pr-tooltip={`UTC ${getUTCOffset(date)}`} data-pr-position="right">
                {formattedDate}
            </span>
        </>
    );
};

// const tableTimeTemplate = function (rowData: {
//     [x: string]: any;
//     active: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactPortal | Iterable<React.ReactNode> | null | undefined;
// }, item: { field: string }) {
//     let nestedValue = getNestedValue(rowData, item.field);
//     let date = null;
//     if (nestedValue !== null && nestedValue !== undefined) {
//         date = new Date();
//         let tempTime = getNestedValue(rowData, item.field).split(":");
//         date.setUTCHours(tempTime[0]);
//         date.setUTCMinutes(tempTime[1]);
//         date.setUTCSeconds(tempTime[2]);
//     }
//
//         // const date = new Date(getNestedValue(rowData, item.field));
//         const formattedDate = date === null ? null : format(date, 'HH:mm:ss');
//     return (
//         <>
//             <Tooltip target="[data-pr-tooltip]"/>
//             <span id={`tooltip-${getNestedValue(rowData, item.field)}`} data-pr-tooltip={`UTC ${date === null ? '' : getUTCOffset(date)}`} data-pr-position="right">
//                 {formattedDate}
//             </span>
//         </>
//     );
// };

function tableActionsTemplate(title: string, fetchData: () => void) {
    const t = i18n.t;

    return <div>
        <h3>{t(title)}</h3>
        <Button onClick={fetchData}>{t("common.search")}</Button>
    </div>;
}

function tableActionDeleteTemplate(event: any, deleteAction: () => void) {
    const t = i18n.t;

    confirmPopup({
        target: event.currentTarget,
        message: t('common.action.delete.confirm'),
        accept: deleteAction
    })
}

function tableActionUnmarkTemplate(event: any, unmarkAction: () => void) {
    const t = i18n.t;

    confirmPopup({
        target: event.currentTarget,
        message: t('common.action.unmark.confirm'),
        accept: unmarkAction
    })
}

function tableHeaderTemplate(title: string, fetchData?: () => void, nodes?: React.ReactNode) {
    const t = i18n.t;

    return <div>
        <h3>{t(title)}</h3>
        {fetchData && <Button onClick={fetchData}>{t("common.refresh")}</Button>}
        {nodes}
    </div>;
}

function tableFooterTemplate(addAction: () => void) {
    const t = i18n.t;

    return <Button onClick={addAction}>{t("common.add")}</Button>;
}

export {
    useTranslation,
    tableTranslationTemplate,
    tableBooleanTemplate,
    tableNumberTemplate,
    tableDecimalTemplate,
    tableDurationTemplate,
    tableDateTimeTemplate,
    tableWeekdayTemplate,
    tableDateTemplate,
    tableTimeTemplate,
    tableActionUnmarkTemplate,
    tableActionDeleteTemplate,
    tableHeaderTemplate,
    tableFooterTemplate,
    tableIconTemplate,
	tableTagTemplate,
	tableTagRandomColorTemplate,
}